import { useCallback, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { persistPanelCollapsed, readPanelCollapsed } from './panel-collapse-storage'

/**
 * The designer's collapsible-panel primitives (issue #189): a persisted
 * collapse state hook, the chevron toggle button every panel hangs off, a
 * header+body section for stacked panels, and a slim rail for panels that
 * collapse sideways.
 *
 * Panels compose these rather than inheriting one fat component, because the
 * three shapes differ structurally: a stacked section keeps its header and
 * drops its body; a side panel is REPLACED by a rail; the preview half is
 * swapped out by SplitPane itself. What they share is the toggle's semantics
 * — `aria-expanded`, `aria-controls`, an interpolated accessible label and a
 * chevron that flips 180° — which is exactly what lives here.
 */

export type ChevronDirection = 'up' | 'down' | 'left' | 'right'

const OPPOSITE: Record<ChevronDirection, ChevronDirection> = {
  up: 'down',
  down: 'up',
  left: 'right',
  right: 'left',
}

/** DOM id of the region a panel's toggle shows/hides (its `aria-controls`). */
export function panelBodyId(panelId: string): string {
  return `panel-${panelId}-body`
}

export interface PanelCollapseState {
  collapsed: boolean
  /** User-initiated flip. Moves focus to the replacement toggle — see below. */
  toggle: () => void
  /** Programmatic set. Deliberately does NOT move focus. */
  setCollapsed: (next: boolean) => void
  /**
   * Ref that MUST be attached to every shape this panel's toggle takes (the
   * expanded header button AND the rail/strip button), so focus survives the
   * swap. Panels whose toggle is never unmounted (CollapsibleSection keeps
   * its header) can ignore it.
   */
  toggleRef: (node: HTMLButtonElement | null) => void
}

/**
 * Collapsed state for one panel, persisted under its own localStorage key.
 *
 * The initial read is a lazy `useState` initialiser, NOT a mount effect: an
 * effect would paint the panel expanded for one frame and then snap it shut
 * on every reload (the flash UserMenu's theme toggle still has).
 */
export function usePanelCollapsed(panelId: string): PanelCollapseState {
  const [collapsed, setState] = useState(() => readPanelCollapsed(panelId))

  // FOCUS RESTORATION. Four of the five #189 panels REPLACE the toggle's DOM
  // node when they collapse — the palette and the properties panel become a
  // rail, the ZPL column becomes a rail, the preview half becomes SplitPane's
  // strip. The button the operator just activated is therefore unmounted, and
  // without this focus falls to <body>: a keyboard operator who collapses the
  // palette is dumped at the start of the document and has to tab the whole
  // page back to the expand affordance. Same on the way back out.
  //
  // A one-shot ref rather than React's `autoFocus`, which fires on EVERY
  // mount of the button: ZplViewer swaps its whole body out the first time
  // ZPL arrives, and an `autoFocus` toggle would steal focus from whatever
  // the operator was typing in at that moment. This fires exactly once per
  // user toggle, and never on the localStorage-restored initial mount (a
  // reload must not steal focus either).
  const focusPending = useRef(false)

  const toggleRef = useCallback((node: HTMLButtonElement | null) => {
    if (node && focusPending.current) {
      focusPending.current = false
      node.focus()
    }
  }, [])

  // Refs attach bottom-up BEFORE an ancestor's layout effects run, so by the
  // time this fires the replacement toggle — always a descendant of the panel
  // owning the hook — has already consumed the request. What is left is a
  // toggle that survived the flip and still holds focus by itself; dropping
  // the flag stops it firing on some later, unrelated remount.
  useLayoutEffect(() => {
    focusPending.current = false
  })

  const setCollapsed = useCallback(
    (next: boolean) => {
      setState(next)
      persistPanelCollapsed(panelId, next)
    },
    [panelId]
  )

  const toggle = useCallback(() => {
    focusPending.current = true
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])

  return { collapsed, toggle, setCollapsed, toggleRef }
}

function Chevron({ direction }: { direction: ChevronDirection }) {
  // Rotated via an SVG transform rather than a Tailwind class so the glyph
  // orientation is inspectable in jsdom and independent of the CSS build.
  const transform =
    direction === 'down'
      ? 'rotate(90 8 8)'
      : direction === 'up'
        ? 'rotate(-90 8 8)'
        : direction === 'left'
          ? 'rotate(180 8 8)'
          : undefined
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M6 3.5 10.5 8 6 12.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform={transform}
      />
    </svg>
  )
}

export interface PanelCollapseToggleProps {
  collapsed: boolean
  onToggle: () => void
  /** Already-translated panel name, interpolated into the accessible label. */
  panelLabel: string
  /**
   * DOM id of the region this button shows/hides — see `panelBodyId`. Emitted
   * as `aria-controls` only while expanded; every panel here UNMOUNTS that
   * region on collapse, so there is nothing for the id to point at.
   */
  controls: string
  testId: string
  /** Chevron direction while EXPANDED; it flips 180° while collapsed. */
  expandedChevron: ChevronDirection
  className?: string
  /**
   * `usePanelCollapsed().toggleRef`. Required on any toggle that is unmounted
   * by its own panel's collapse, so focus lands on the button that replaces
   * it instead of falling to <body>.
   */
  buttonRef?: Ref<HTMLButtonElement>
}

/**
 * The one control that collapses/expands a panel. A native `<button>`, so
 * Enter/Space and focus order come for free — and, via `buttonRef`, focus
 * order survives the panels that swap the button out (see `usePanelCollapsed`).
 *
 * `aria-controls` is emitted ONLY while expanded. All five #189 panels drop
 * the controlled region from the DOM when they collapse — the stacked section
 * renders no body, and the side/preview panels are replaced wholesale by a
 * rail or a strip — so a collapsed toggle that kept the attribute pointed at
 * an id no element carries. That is a broken IDREF: assistive tech resolves
 * `aria-controls` against the live tree and announces nothing (or worse,
 * offers a "go to controlled region" command that lands nowhere), which is
 * strictly less useful than the attribute being absent. `aria-expanded` plus
 * the "Expand {panel}" label already carry the disclosure semantics in the
 * collapsed state, and `aria-controls` comes back the moment the region does.
 */
export function PanelCollapseToggle({
  collapsed,
  onToggle,
  panelLabel,
  controls,
  testId,
  expandedChevron,
  className,
  buttonRef,
}: PanelCollapseToggleProps) {
  const { t } = useTranslation()
  const label = t(collapsed ? 'collapsible.expand' : 'collapsible.collapse', {
    panel: panelLabel,
  })
  return (
    <button
      ref={buttonRef}
      type="button"
      data-testid={testId}
      aria-expanded={!collapsed}
      // undefined omits the attribute entirely — see the note above.
      aria-controls={collapsed ? undefined : controls}
      aria-label={label}
      title={label}
      onClick={onToggle}
      className={[
        'flex h-5 w-5 shrink-0 items-center justify-center rounded text-stone-500',
        'hover:bg-stone-100 hover:text-stone-900 focus:outline-none focus-visible:ring-1',
        'focus-visible:ring-sky-500 dark:hover:bg-stone-800 dark:hover:text-stone-100',
        className ?? '',
      ]
        .join(' ')
        .trim()}
    >
      <Chevron direction={collapsed ? OPPOSITE[expandedChevron] : expandedChevron} />
    </button>
  )
}

export interface CollapsibleSectionProps {
  /** Stable id — drives the storage key, the testids and `aria-controls`. */
  panelId: string
  /** Already-translated section title. */
  title: string
  collapsed: boolean
  onToggle: () => void
  /** Controls pinned to the right of the header, visible while collapsed. */
  headerExtra?: ReactNode
  className?: string
  headerClassName?: string
  /**
   * The panel body. Rendered only while expanded, and expected to carry
   * `id={panelBodyId(panelId)}` — the body element belongs to the caller so
   * that panels with pinned layout contracts (e.g. the #151
   * `max-h-48 shrink-0 overflow-y-auto` block table) keep owning their own
   * classes.
   */
  children: ReactNode
}

/** A stacked panel: header bar with the toggle, body below it. */
export function CollapsibleSection({
  panelId,
  title,
  collapsed,
  onToggle,
  headerExtra,
  className,
  headerClassName,
  children,
}: CollapsibleSectionProps) {
  return (
    <div data-testid={`${panelId}-panel`} className={className}>
      <div
        data-testid={`${panelId}-header`}
        className={['flex items-center gap-1.5', headerClassName ?? ''].join(' ').trim()}
      >
        <PanelCollapseToggle
          collapsed={collapsed}
          onToggle={onToggle}
          panelLabel={title}
          controls={panelBodyId(panelId)}
          testId={`${panelId}-toggle`}
          expandedChevron="down"
        />
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {title}
        </h3>
        {headerExtra ? <div className="ml-auto flex items-center gap-2">{headerExtra}</div> : null}
      </div>
      {!collapsed && children}
    </div>
  )
}

export interface CollapsedRailProps {
  /** Same id the expanded panel uses, so the toggle testid is stable. */
  panelId: string
  /** Already-translated panel name, shown vertically down the rail. */
  label: string
  onExpand: () => void
  /** Which edge of the layout the rail sits on: sets border side + chevron. */
  side: 'left' | 'right'
  className?: string
  testId?: string
  /**
   * `usePanelCollapsed().toggleRef` — the rail's toggle is the node that
   * replaces the collapsed panel's own toggle, so it takes over its focus.
   */
  toggleRef?: Ref<HTMLButtonElement>
}

/**
 * What a side panel collapses to: a slim vertical strip carrying the expand
 * button and the panel name. Deliberately a plain `<div>` and not an
 * `<aside>` — one of these renders inside PreviewPane's own `<aside>`, and a
 * nested complementary landmark buys nothing when the expand button already
 * announces itself as "Expand {panel}".
 */
export function CollapsedRail({
  panelId,
  label,
  onExpand,
  side,
  className,
  testId,
  toggleRef,
}: CollapsedRailProps) {
  return (
    <div
      data-testid={testId ?? `${panelId}-rail`}
      className={[
        'flex w-9 shrink-0 flex-col items-center gap-2 overflow-hidden bg-white py-2 dark:bg-stone-900',
        side === 'left'
          ? 'border-r border-stone-200 dark:border-stone-800'
          : 'border-l border-stone-200 dark:border-stone-800',
        className ?? '',
      ]
        .join(' ')
        .trim()}
    >
      <PanelCollapseToggle
        collapsed
        onToggle={onExpand}
        panelLabel={label}
        controls={panelBodyId(panelId)}
        testId={`${panelId}-toggle`}
        expandedChevron={side === 'left' ? 'left' : 'right'}
        buttonRef={toggleRef}
      />
      <span
        aria-hidden="true"
        style={{ writingMode: 'vertical-rl' }}
        className="select-none whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-stone-500"
      >
        {label}
      </span>
    </div>
  )
}
